import { useMemo } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { abbreviate } from '@chainflip/utils/string';
import BigNumber from 'bignumber.js';
import classNames from 'classnames';
import { Link } from '@/shared/components/atoms/Link';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { type BoostSharesMap, type BoostBalance } from '@/shared/hooks/useBoostPool';
import useFrontendPagination from '@/shared/hooks/useFrontendPagination';
import useTableSort from '@/shared/hooks/useTableSort';
import { chainflipAssetMap } from '@/shared/utils/env';
import { COMPARATORS } from '@/shared/utils/helpers';
import { formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

const DEFAULT_SORT = { col: 1, direction: 'DESC' as 'ASC' | 'DESC' };

const Row = ({
  boostBalance,
  asset,
  earnedFees,
  blockExplorerUrl,
  highlight,
}: {
  boostBalance: Partial<BoostBalance>;
  asset: ChainflipAsset;
  earnedFees: string | undefined;
  blockExplorerUrl?: string;
  highlight: boolean;
}) => {
  const lpPath = `/lps/${boostBalance.lpIdSs58}`;
  const lpUrl = blockExplorerUrl ? new URL(lpPath, blockExplorerUrl).toString() : lpPath;

  const amount = TokenAmount.fromAsset(boostBalance.availableAmount ?? 0, asset)
    .add(boostBalance.unavailableAmount ?? 0)
    .toFormatted();

  const usdValue = BigNumber.sum(
    boostBalance.availableAmountValueUsd ?? 0,
    boostBalance.unavailableAmountValueUsd ?? 0,
  ).toNumber();

  return (
    <ClickableTableRow
      key={boostBalance.lpIdSs58}
      className={classNames('text-cf-light-3', highlight && 'bg-cf-gray-4 hover:bg-[#303030]')}
      href={lpUrl}
      forceNewTab={Boolean(blockExplorerUrl)}
    >
      <td className="whitespace-nowrap">
        <div className="flex items-center gap-x-2">
          <Link
            href={lpUrl}
            underline
            target={blockExplorerUrl ? '_blank' : undefined}
            noPropagate
            className="text-14"
          >
            {abbreviate(boostBalance.lpIdSs58, 8)}
          </Link>
          <CopyButton textToCopy={boostBalance.lpIdSs58 as string} />
        </div>
      </td>
      <td className="whitespace-nowrap">
        <div className="flex flex-col gap-y-px text-14">
          <span className="flex items-center gap-x-2 text-cf-light-3">
            <TokenWithChainByAsset asset={asset} />
            {amount} {chainflipAssetMap[asset].symbol}
          </span>
          <span className="text-cf-light-2">{formatUsdValue(usdValue)}</span>
        </div>
      </td>
      <td className="whitespace-nowrap">{formatUsdValue(earnedFees ?? 0)}</td>
      <td className="whitespace-nowrap text-right">{boostBalance.poolShare?.toFixed(2)} %</td>
    </ClickableTableRow>
  );
};

export const LpBoosterTable = ({
  boostBalances,
  boostShares,
  isLoading,
  blockExplorerUrl,
  highlightedAccountId,
}: {
  boostBalances: BoostBalance[];
  boostShares: BoostSharesMap;
  isLoading: boolean;
  blockExplorerUrl?: string;
  highlightedAccountId?: string;
}) => {
  const { setCol, sort } = useTableSort({ initialSort: DEFAULT_SORT });

  const SORT_FUNCTIONS: ((props: BoostBalance) => number)[] = [
    () => 0,
    (props) => Number(props.availableAmountValueUsd) + Number(props.unavailableAmountValueUsd),
    (props) => Number(boostShares?.get(props.lpIdSs58)?.feeUsd),
    (props) => props.poolShare,
  ];

  const sortedRows = useMemo(() => {
    if (!boostBalances || !boostShares) return [];

    return boostBalances.toSorted((a, b) =>
      COMPARATORS[sort.direction](SORT_FUNCTIONS[sort.col](a), SORT_FUNCTIONS[sort.col](b)),
    );
  }, [boostBalances, sort, boostShares]);

  const { data: paginatedBalances, paginationControls } = useFrontendPagination(sortedRows);

  return (
    <TableV2
      isLoading={isLoading}
      columns={[
        { name: 'Account ID', sortable: false },
        { name: 'Boost Liquidity', sortable: true },
        { name: 'Earned Fees', sortable: true },
        { name: 'Pool Share', sortable: true, alignment: 'right' },
      ]}
      rows={paginatedBalances.edges.map(({ node: balance }) => (
        <Row
          key={balance.lpIdSs58}
          boostBalance={balance}
          asset={balance.boostPool.asset}
          earnedFees={boostShares?.get(balance.lpIdSs58)?.feeUsd}
          blockExplorerUrl={blockExplorerUrl}
          highlight={balance.lpIdSs58 === highlightedAccountId}
        />
      ))}
      paginationControls={paginationControls}
      sorting={{ setCol, sort }}
    />
  );
};
